import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, EnvelopeSimple, ArrowLeft, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { formatApiErrorDetail } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

import { API_URL } from '../config/api';

const pageAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 },
};

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to process request');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <motion.div
        initial={pageAnimation.initial}
        animate={pageAnimation.animate}
        transition={pageAnimation.transition}
        className="relative z-10 w-full max-w-md"
      >
        <Card className="shadow-xl border-slate-200/60">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-[#0B2545] rounded-xl flex items-center justify-center">
              <ShieldCheck size={32} weight="duotone" className="text-white" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">
              Reset Password
            </CardTitle>
            <CardDescription className="text-slate-600">
              Enter your email to receive a password reset link
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {success ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700">
                  <CheckCircle size={22} weight="fill" className="flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Check your email</p>
                    <p className="text-xs mt-0.5">If an account exists for <strong>{email}</strong>, we've sent a password reset link. Please check your inbox and spam folder.</p>
                  </div>
                </div>

                <Link to="/login">
                  <Button variant="outline" className="w-full mt-2" data-testid="back-to-login-button">
                    <ArrowLeft size={18} className="mr-2" /> Back to Sign In
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm"
                  >
                    <WarningCircle size={18} weight="fill" />
                    <span>{error}</span>
                  </motion.div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-sm font-medium text-slate-700">
                    Email Address
                  </Label>
                  <div className="relative">
                    <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11 pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-11 bg-[#0B2545] hover:bg-[#13315C] text-white font-medium"
                  data-testid="forgot-submit-button"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full text-slate-600" data-testid="forgot-back-to-login">
                    <ArrowLeft size={18} className="mr-2" /> Back to Sign In
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
